import React, { useState, useEffect } from 'react';
import { Users, DollarSign, Link2, Copy, Check, TrendingUp, Clock, Wallet, ArrowRight, AlertCircle, CreditCard, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AffiliateSection = () => {
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: '',
    payment_method: 'crypto',
    cryptocurrency: 'USDT',
    wallet_address: '',
    bank_name: '',
    account_name: '',
    account_number: '',
    swift_code: ''
  });
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState('');

  useEffect(() => {
    fetchAffiliateData();
  }, []);

  const fetchAffiliateData = async () => {
    try {
      const [statsRes, referralsRes, withdrawalsRes] = await Promise.all([
        axios.get(`${API_URL}/affiliate/stats`),
        axios.get(`${API_URL}/affiliate/referrals`),
        axios.get(`${API_URL}/affiliate/withdrawals`)
      ]);
      
      setStats(statsRes.data);
      setReferrals(referralsRes.data);
      setWithdrawals(withdrawalsRes.data);
    } catch (error) {
      console.error('Error fetching affiliate data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    if (stats?.referral_link) {
      await navigator.clipboard.writeText(stats.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    setWithdrawError('');
    setWithdrawLoading(true);

    try {
      const params = new URLSearchParams();
      params.append('amount', withdrawForm.amount);
      params.append('payment_method', withdrawForm.payment_method);
      
      if (withdrawForm.payment_method === 'crypto') {
        params.append('cryptocurrency', withdrawForm.cryptocurrency);
        params.append('wallet_address', withdrawForm.wallet_address);
      } else {
        params.append('bank_name', withdrawForm.bank_name);
        params.append('account_name', withdrawForm.account_name);
        params.append('account_number', withdrawForm.account_number);
        if (withdrawForm.swift_code) {
          params.append('swift_code', withdrawForm.swift_code);
        }
      }

      await axios.post(`${API_URL}/affiliate/withdraw?${params.toString()}`);
      
      alert('Withdrawal request submitted successfully!');
      setWithdrawModalOpen(false);
      fetchAffiliateData();
      setWithdrawForm({
        ...withdrawForm,
        amount: '',
        wallet_address: '',
        bank_name: '',
        account_name: '',
        account_number: '',
        swift_code: ''
      });
    } catch (error) {
      setWithdrawError(error.response?.data?.detail || 'Failed to submit withdrawal request');
    } finally {
      setWithdrawLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
        Loading affiliate data...
      </div>
    );
  }

  const canWithdraw = stats?.withdrawable_balance >= (stats?.min_withdrawal || 150);

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.2), rgba(14, 165, 233, 0.1))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Users size={24} color="var(--accent-blue)" />
          </div>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>
              Affiliate Program
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
              Earn {stats?.commission_rate || 10}% commission on referrals
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          background: 'var(--bg-tertiary)',
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid var(--border-subtle)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Users size={18} color="var(--accent-blue)" />
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Total Referrals</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)' }}>
            {stats?.total_referrals || 0}
          </div>
        </div>

        <div style={{
          background: 'var(--bg-tertiary)',
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid var(--border-subtle)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <TrendingUp size={18} color="var(--success)" />
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Total Earned</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--success)' }}>
            ${(stats?.total_earned || 0).toFixed(2)}
          </div>
        </div>

        <div style={{
          background: 'var(--bg-tertiary)',
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid var(--border-subtle)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Wallet size={18} color="var(--accent-gold)" />
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Withdrawable</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--accent-gold)' }}>
            ${(stats?.withdrawable_balance || 0).toFixed(2)}
          </div>
          {!canWithdraw && stats?.withdrawable_balance > 0 && (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Min. ${stats?.min_withdrawal || 150} required
            </div>
          )}
        </div>

        <div style={{
          background: 'var(--bg-tertiary)',
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid var(--border-subtle)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Clock size={18} color="var(--warning)" />
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Pending</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--warning)' }}>
            ${(stats?.pending_balance || 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Referral Link */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(212, 175, 55, 0.1))',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid rgba(14, 165, 233, 0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Link2 size={18} color="var(--accent-blue)" />
          <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
            Your Referral Link
          </span>
        </div>
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            flex: 1,
            minWidth: '200px',
            background: 'var(--bg-primary)',
            borderRadius: '10px',
            padding: '14px 16px',
            fontSize: '14px',
            color: 'var(--text-secondary)',
            wordBreak: 'break-all'
          }}>
            {stats?.referral_link || 'Loading...'}
          </div>
          <Button
            onClick={copyReferralLink}
            className={copied ? '' : 'btn-primary'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 24px',
              background: copied ? 'var(--success)' : undefined
            }}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copied!' : 'Copy Link'}
          </Button>
        </div>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '12px' }}>
          Share this link and earn {stats?.commission_rate || 10}% commission when your referrals make a purchase!
        </p>
      </div>

      {/* Withdraw Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
            Commission Withdrawals
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            Minimum withdrawal: ${stats?.min_withdrawal || 150}
          </p>
        </div>
        <Button
          onClick={() => setWithdrawModalOpen(true)}
          disabled={!canWithdraw}
          className={canWithdraw ? 'btn-primary' : ''}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            opacity: canWithdraw ? 1 : 0.5
          }}
        >
          <DollarSign size={16} />
          Request Withdrawal
        </Button>
      </div>

      {/* Referrals List */}
      {referrals.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Your Referrals
          </h3>
          <div style={{
            background: 'var(--bg-tertiary)',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid var(--border-subtle)'
          }}>
            {referrals.map((ref, index) => (
              <div key={ref.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                borderBottom: index < referrals.length - 1 ? '1px solid var(--border-subtle)' : 'none'
              }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)' }}>
                    {ref.referred_name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {ref.referred_email} • Joined {new Date(ref.registered_at).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {ref.has_purchased ? (
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--success)' }}>
                      +${ref.commission_earned.toFixed(2)}
                    </div>
                  ) : (
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-muted)',
                      background: 'var(--bg-primary)',
                      padding: '4px 10px',
                      borderRadius: '12px'
                    }}>
                      No purchase yet
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Withdrawal History */}
      {withdrawals.length > 0 && (
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Withdrawal History
          </h3>
          <div style={{
            background: 'var(--bg-tertiary)',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid var(--border-subtle)'
          }}>
            {withdrawals.map((w, index) => (
              <div key={w.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                borderBottom: index < withdrawals.length - 1 ? '1px solid var(--border-subtle)' : 'none'
              }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                    ${w.amount.toFixed(2)}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {w.payment_method === 'crypto' ? `${w.cryptocurrency}` : 'Bank Transfer'} • {new Date(w.requested_at).toLocaleDateString()}
                  </div>
                </div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '6px 12px',
                  borderRadius: '20px',
                  background: w.status === 'paid' ? 'rgba(34, 197, 94, 0.1)' :
                             w.status === 'approved' ? 'rgba(14, 165, 233, 0.1)' :
                             w.status === 'declined' ? 'rgba(239, 68, 68, 0.1)' :
                             'rgba(245, 158, 11, 0.1)',
                  color: w.status === 'paid' ? 'var(--success)' :
                         w.status === 'approved' ? 'var(--accent-blue)' :
                         w.status === 'declined' ? 'var(--error)' :
                         'var(--warning)',
                  textTransform: 'capitalize'
                }}>
                  {w.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Withdrawal Modal */}
      {withdrawModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }} onClick={() => setWithdrawModalOpen(false)}>
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '480px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: '1px solid var(--border-subtle)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
              Request Withdrawal
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '24px' }}>
              Available: ${(stats?.withdrawable_balance || 0).toFixed(2)}
            </p>

            {withdrawError && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <AlertCircle size={18} color="var(--error)" />
                <span style={{ fontSize: '14px', color: 'var(--error)' }}>{withdrawError}</span>
              </div>
            )}

            <form onSubmit={handleWithdrawSubmit}>
              {/* Amount */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Amount (USD)
                </label>
                <input
                  type="number"
                  min={stats?.min_withdrawal || 150}
                  max={stats?.withdrawable_balance}
                  step="0.01"
                  value={withdrawForm.amount}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: '10px',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                  placeholder={`Min. $${stats?.min_withdrawal || 150}`}
                />
              </div>

              {/* Payment Method */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Payment Method
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setWithdrawForm({ ...withdrawForm, payment_method: 'crypto' })}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '14px',
                      background: withdrawForm.payment_method === 'crypto' ? 'rgba(212, 175, 55, 0.1)' : 'var(--bg-primary)',
                      border: `1px solid ${withdrawForm.payment_method === 'crypto' ? 'var(--accent-gold)' : 'var(--border-primary)'}`,
                      borderRadius: '10px',
                      color: withdrawForm.payment_method === 'crypto' ? 'var(--accent-gold)' : 'var(--text-secondary)',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    <CreditCard size={18} />
                    Crypto
                  </button>
                  <button
                    type="button"
                    onClick={() => setWithdrawForm({ ...withdrawForm, payment_method: 'bank' })}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '14px',
                      background: withdrawForm.payment_method === 'bank' ? 'rgba(212, 175, 55, 0.1)' : 'var(--bg-primary)',
                      border: `1px solid ${withdrawForm.payment_method === 'bank' ? 'var(--accent-gold)' : 'var(--border-primary)'}`,
                      borderRadius: '10px',
                      color: withdrawForm.payment_method === 'bank' ? 'var(--accent-gold)' : 'var(--text-secondary)',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    <Building2 size={18} />
                    Bank
                  </button>
                </div>
              </div>

              {/* Crypto Fields */}
              {withdrawForm.payment_method === 'crypto' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                      Cryptocurrency
                    </label>
                    <select
                      value={withdrawForm.cryptocurrency}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, cryptocurrency: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '10px',
                        color: 'var(--text-primary)',
                        fontSize: '14px'
                      }}
                    >
                      <option value="USDT">USDT (TRC20)</option>
                      <option value="BTC">Bitcoin (BTC)</option>
                      <option value="ETH">Ethereum (ETH)</option>
                      <option value="LTC">Litecoin (LTC)</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                      Wallet Address
                    </label>
                    <input
                      type="text"
                      value={withdrawForm.wallet_address}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, wallet_address: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '10px',
                        color: 'var(--text-primary)',
                        fontSize: '14px'
                      }}
                      placeholder="Enter your wallet address"
                    />
                  </div>
                </>
              )}

              {/* Bank Fields */}
              {withdrawForm.payment_method === 'bank' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                      Bank Name
                    </label>
                    <input
                      type="text"
                      value={withdrawForm.bank_name}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_name: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '10px',
                        color: 'var(--text-primary)',
                        fontSize: '14px'
                      }}
                      placeholder="Enter bank name"
                    />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                      Account Holder Name
                    </label>
                    <input
                      type="text"
                      value={withdrawForm.account_name}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, account_name: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '10px',
                        color: 'var(--text-primary)',
                        fontSize: '14px'
                      }}
                      placeholder="Enter account holder name"
                    />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={withdrawForm.account_number}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, account_number: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '10px',
                        color: 'var(--text-primary)',
                        fontSize: '14px'
                      }}
                      placeholder="Enter account number"
                    />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                      SWIFT/BIC Code (Optional)
                    </label>
                    <input
                      type="text"
                      value={withdrawForm.swift_code}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, swift_code: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '10px',
                        color: 'var(--text-primary)',
                        fontSize: '14px'
                      }}
                      placeholder="For international transfers"
                    />
                  </div>
                </>
              )}

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={() => setWithdrawModalOpen(false)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'transparent',
                    border: '1px solid var(--border-primary)',
                    borderRadius: '10px',
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <Button
                  type="submit"
                  disabled={withdrawLoading}
                  className="btn-primary"
                  style={{ flex: 1, padding: '14px' }}
                >
                  {withdrawLoading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateSection;
